import { useSyncExternalStore } from 'react';

interface LegacyMediaQueryList extends MediaQueryList {
  addListener: (listener: (event: MediaQueryListEvent) => void) => void;
  removeListener: (listener: (event: MediaQueryListEvent) => void) => void;
}

const subscribeToMediaQuery =
  (mediaQuery: string) => (onStoreChange: () => void) => {
    if (typeof window === 'undefined') {
      return () => undefined;
    }

    const mediaQueryList = window.matchMedia(mediaQuery);
    const handleChange = () => onStoreChange();

    if ('addEventListener' in mediaQueryList) {
      mediaQueryList.addEventListener('change', handleChange);

      return () => mediaQueryList.removeEventListener('change', handleChange);
    }

    const legacyMediaQueryList = mediaQueryList as LegacyMediaQueryList;

    legacyMediaQueryList.addListener(handleChange);

    return () => legacyMediaQueryList.removeListener(handleChange);
  };

const getMediaQuerySnapshot = (mediaQuery: string) => (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.matchMedia(mediaQuery).matches;
};

/**
 * Shared hook that tracks whether a CSS media query currently matches.
 *
 * @param mediaQuery - Media query string to observe.
 * @returns Whether the query currently matches.
 */
export const useMediaQuery = (mediaQuery: string): boolean => {
  return useSyncExternalStore(
    subscribeToMediaQuery(mediaQuery),
    getMediaQuerySnapshot(mediaQuery),
    () => false
  );
};
